import { useEffect, useState } from 'react';

const DEFAULT_SPRITE_SIZE = 68;
const DESKTOP_COLUMNS = 8;
const TABLET_COLUMNS = 6;
const MOBILE_COLUMNS = 4;
const ROW_HEIGHT = 140;
const VIEWPORT_HEIGHT_MULTIPLIER = 1.15;
const OFFSCREEN_OFFSET = 10;
const SPRITE_TRAVEL_DURATION = 150;

function getRandom(min, max) {
  return Math.random() * (max - min) + min;
}

function getRandomItem(items) {
  return items[Math.floor(Math.random() * items.length)];
}

function getColumnCount(viewportWidth) {
  if (viewportWidth <= 640) {
    return MOBILE_COLUMNS;
  }

  if (viewportWidth <= 900) {
    return TABLET_COLUMNS;
  }

  return DESKTOP_COLUMNS;
}

function buildSprites(spriteNames, viewportHeight, viewportWidth) {
  const columnCount = getColumnCount(viewportWidth);
  const rowCount = Math.max(1, Math.ceil((viewportHeight * VIEWPORT_HEIGHT_MULTIPLIER) / ROW_HEIGHT));
  const spriteCount = columnCount * rowCount;

  return {
    columnCount,
    rowCount,
    sprites: Array.from({ length: spriteCount }, (_, index) => {
      const column = index % columnCount;
      const row = Math.floor(index / columnCount);

      return {
        id: index,
        src: `/images/pokesprites/${getRandomItem(spriteNames)}`,
        top: row * ROW_HEIGHT + ROW_HEIGHT / 2,
        left: -OFFSCREEN_OFFSET,
        size: DEFAULT_SPRITE_SIZE,
        opacity: getRandom(0.18, 0.34),
        duration: SPRITE_TRAVEL_DURATION,
        delay: -((SPRITE_TRAVEL_DURATION / columnCount) * column),
        rotation: getRandom(-10, 10),
      };
    }),
  };
}

export default function Background() {
  const [spriteNames, setSpriteNames] = useState([]);
  const [sprites, setSprites] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function loadSprites() {
      const response = await fetch('/images/pokesprites/index.json');
      const nextSpriteNames = await response.json();

      if (cancelled || !Array.isArray(nextSpriteNames) || nextSpriteNames.length === 0) {
        return;
      }

      setSpriteNames(nextSpriteNames);

      const updateSprites = () => {
        const nextGrid = buildSprites(
          nextSpriteNames,
          window.innerHeight,
          window.innerWidth
        );

        setSprites(nextGrid.sprites);
      };

      updateSprites();
      window.addEventListener('resize', updateSprites);

      return () => {
        window.removeEventListener('resize', updateSprites);
      };
    }

    let cleanupResizeListener;

    loadSprites()
      .then((cleanup) => {
        cleanupResizeListener = cleanup;
      })
      .catch(() => {
        if (!cancelled) {
          setSprites([]);
        }
      });

    return () => {
      cancelled = true;
      if (cleanupResizeListener) {
        cleanupResizeListener();
      }
    };
  }, []);

  function handleSpriteLoop(spriteId) {
    if (sprites.length === 0 || spriteNames.length === 0) {
      return;
    }

    setSprites((currentSprites) =>
      currentSprites.map((sprite) => {
        if (sprite.id !== spriteId) {
          return sprite;
        }

        return {
          ...sprite,
          src: `/images/pokesprites/${getRandomItem(spriteNames)}`,
          opacity: getRandom(0.18, 0.34),
          rotation: getRandom(-10, 10),
        };
      })
    );
  }

  return (
    <div className="poke-background" aria-hidden="true">
      {sprites.map((sprite) => (
        <img
          className="poke-background__sprite"
          key={sprite.id}
          src={sprite.src}
          alt=""
          onAnimationIteration={() => handleSpriteLoop(sprite.id)}
          style={{
            top: `${sprite.top}px`,
            left: `${sprite.left}%`,
            width: `${sprite.size}px`,
            opacity: sprite.opacity,
            '--sprite-duration': `${sprite.duration}s`,
            '--sprite-delay': `${sprite.delay}s`,
            '--sprite-rotation': `${sprite.rotation}deg`,
          }}
        />
      ))}
    </div>
  );
}
